import { api, decodeVaultBlobResponse, type EmailNotificationType } from '../api/client';
import { guestApi } from '../api/guest';
import {
  buildGuestSettingsUrl,
  parseGuestSettingsUrl,
  type GuestSettingsSection,
} from '../app/navigationState';
import { changeVaultPassphrase } from '../crypto/passphraseFlow';
import { isForcePassphraseChangeRequired, setForcePassphraseChange } from '../crypto/passphraseAge';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import { createAndPublishRecoveryPackage } from '../crypto/recoveryFlow';
import { deriveGuestLoginVerifierFromPassphrase, encodeLoginVerifierBase64 } from '../crypto/guestLoginVerifier';
import { loadVaultSession } from '../crypto/vaultSession';
import { unlockVaultWithPassphrase } from '../crypto/vault';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { createHelpTip } from '../help/helpTips';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { setGuestShellName } from '../ui/guestShellName';
import { attachPassphraseWhitespaceWarnings } from '../ui/passphraseWhitespaceWarning';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from '../ui/passwordPolicyUi';
import { showRecoveryExportDialog } from '../ui/recoveryExportDialog';
import { mountSectionNav } from '../ui/sectionNav';
import { renderSVG } from 'uqr';

const GUEST_NOTIFICATION_TYPES: EmailNotificationType[] = [
  'sedrGuestRoomAdded',
  'sedrGuestAccountReady',
  'recoveryPackageCreated',
  'recoveryPackageReplaced',
  'recoveryPackageInvalidated',
];

export function GuestSettingsPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--settings module-page--guest-portal';
  page.dataset.guestSettingsWorkspace = '';

  const notificationRowsHtml = GUEST_NOTIFICATION_TYPES.map((type) => `
      <label class="checkbox-row settings-notification-row">
        <input type="checkbox" name="notification-${type}" data-notification-type="${type}" checked />
        <span>
          <span class="settings-notification-row__label" data-i18n="settings.notifications.types.${type}.label"></span>
          <span class="muted settings-notification-row__description" data-i18n="settings.notifications.types.${type}.description"></span>
        </span>
      </label>`).join('');

  page.innerHTML = `
    <section class="module-section" id="guest-settings-passphrase-section">
      <div class="module-section__header"><div class="module-section__header-main"><h2 data-i18n="settings.passphrase.title"></h2></div></div>
      <p class="muted" data-i18n="settings.passphrase.subtitle"></p>
      <form class="form-grid" id="guest-settings-passphrase-form">
        <label><span data-i18n="settings.passphrase.current"></span><input type="password" name="current" required autocomplete="current-password" /></label>
        <label><span data-i18n="settings.passphrase.new"></span><input type="password" name="new" minlength="12" required autocomplete="new-password" /></label>
        <label><span data-i18n="settings.passphrase.confirm"></span><input type="password" name="confirm" minlength="12" required autocomplete="new-password" /></label>
        <p class="muted" id="guest-settings-passphrase-policy-hint" hidden></p>
        <p class="warning-text" id="guest-settings-passphrase-expired" hidden data-i18n="passwordPolicy.expired"></p>
        <button type="submit" class="button primary" data-i18n="settings.passphrase.submit"></button>
      </form>
      <p class="success-text" id="guest-settings-passphrase-success" hidden data-i18n="settings.passphrase.success"></p>
      <p class="error-text" id="guest-settings-passphrase-error" hidden></p>
    </section>
    <section class="module-section" id="guest-settings-recovery-section">
      <div class="module-section__header"><div class="module-section__header-main"><h2 data-i18n="settings.recovery.title"></h2></div></div>
      <p class="muted" id="guest-settings-recovery-status"></p>
      <p class="muted" data-i18n="settings.recovery.subtitle"></p>
      <form class="form-grid" id="guest-settings-recovery-form">
        <label><span data-i18n="settings.recovery.passphrase"></span><input type="password" name="passphrase" required autocomplete="current-password" /></label>
        <button type="submit" class="button primary" data-i18n="settings.recovery.create"></button>
      </form>
      <p class="error-text" id="guest-settings-recovery-error" hidden></p>
    </section>
    <section class="module-section" id="guest-settings-notifications-section">
      <div class="module-section__header"><div class="module-section__header-main"><h2 data-i18n="settings.notifications.title"></h2></div></div>
      <p class="muted" data-i18n="guest.settings.emailPrefs"></p>
      <form class="form-grid" id="guest-settings-notifications-form">
        ${notificationRowsHtml}
        <div class="settings-sedr-digests" id="guest-settings-sedr-digests">
          <h3 data-i18n="settings.sedrDigests.title"></h3>
          <div class="settings-sedr-digests__defaults">
            <div class="settings-sedr-digests__defaults-label" data-i18n="settings.sedrDigests.defaultLabel"></div>
            <div class="settings-sedr-digests__toggles">
              <label class="settings-sedr-digests__toggle">
                <input type="checkbox" id="guest-settings-sedr-digest-daily" data-sedr-digest="daily" checked />
                <span data-i18n="settings.sedrDigests.daily.short"></span>
              </label>
              <label class="settings-sedr-digests__toggle">
                <input type="checkbox" id="guest-settings-sedr-digest-biweekly" data-sedr-digest="biweekly" />
                <span data-i18n="settings.sedrDigests.biweekly.short"></span>
              </label>
            </div>
          </div>
          <p class="muted settings-sedr-digests__rule" data-i18n="settings.sedrDigests.rule"></p>
          <div class="settings-sedr-digests__rooms-block">
            <div class="settings-sedr-digests__rooms-head">
              <span data-i18n="settings.sedrDigests.perRoom"></span>
              <span class="settings-sedr-digests__col" data-i18n="settings.sedrDigests.daily.short"></span>
              <span class="settings-sedr-digests__col" data-i18n="settings.sedrDigests.biweekly.short"></span>
              <span class="settings-sedr-digests__col settings-sedr-digests__col--status" aria-hidden="true"></span>
            </div>
            <div id="guest-settings-sedr-digest-rooms" class="settings-sedr-digests__room-list"></div>
          </div>
        </div>
        <button type="submit" class="button primary" data-i18n="guest.settings.save"></button>
      </form>
      <p class="success-text" id="guest-settings-notifications-success" hidden data-i18n="guest.settings.saved"></p>
      <p class="error-text" id="guest-settings-notifications-error" hidden></p>
    </section>
    <section class="module-section" id="guest-settings-totp-section">
      <div class="module-section__header"><div class="module-section__header-main"><h2 data-i18n="guest.settings.totp.title"></h2></div></div>
      <p class="muted" id="guest-settings-totp-status" data-i18n="guest.settings.totp.subtitle"></p>
      <div id="guest-settings-totp-enroll" class="form-grid" hidden>
        <p class="muted" data-i18n="sedr.guest.totpEnrollHint"></p>
        <div id="guest-settings-totp-qr" class="sedr-guest-totp-qr"></div>
        <label><span data-i18n="sedr.guest.totpSecret"></span><input type="text" id="guest-settings-totp-secret" readonly autocomplete="off" /></label>
        <form id="guest-settings-totp-enroll-form" class="form-grid">
          <label><span data-i18n="sedr.guest.totpCode"></span><input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required /></label>
          <button type="submit" class="button primary" data-i18n="sedr.guest.totpConfirm"></button>
        </form>
      </div>
      <div id="guest-settings-totp-actions" class="form-grid">
        <button type="button" class="button primary" id="guest-settings-totp-start" data-i18n="guest.settings.totp.setup"></button>
        <button type="button" class="button" id="guest-settings-totp-replace" hidden data-i18n="guest.settings.totp.replace"></button>
      </div>
      <p class="error-text" id="guest-settings-totp-error" hidden></p>
      <p class="success-text" id="guest-settings-totp-success" hidden></p>
    </section>
  `;
  applyI18n(page);

  void guestApi.session().then((s) => setGuestShellName(s.displayName, s.organization)).catch(() => undefined);

  const passphraseSection = page.querySelector('#guest-settings-passphrase-section') as HTMLElement;
  const recoverySection = page.querySelector('#guest-settings-recovery-section') as HTMLElement;
  const notificationsSection = page.querySelector('#guest-settings-notifications-section') as HTMLElement;
  const totpSection = page.querySelector('#guest-settings-totp-section') as HTMLElement;

  const sectionNav = mountSectionNav(
    page,
    [
      { id: 'passphrase', labelKey: 'settings.passphrase.title', panel: passphraseSection },
      { id: 'recovery', labelKey: 'settings.recovery.title', panel: recoverySection },
      { id: 'notifications', labelKey: 'settings.notifications.title', panel: notificationsSection },
      { id: 'totp', labelKey: 'guest.settings.totp.title', panel: totpSection },
    ],
    {
      navAriaLabelKey: 'nav.settings',
      buildUrl: (id) => buildGuestSettingsUrl(id as GuestSettingsSection),
      readId: () => parseGuestSettingsUrl()?.section ?? null,
    },
  );
  const parsed = parseGuestSettingsUrl();
  if (parsed?.section && sectionNav.getActiveId() !== parsed.section) sectionNav.applyFromUrl();
  page.addEventListener('guest-settings:route', () => {
    sectionNav.applyFromUrl();
    syncHelpContext();
  });

  const syncHelpContext = () => {
    const section = (sectionNav.getActiveId() ?? parseGuestSettingsUrl()?.section) as GuestSettingsSection | null;
    const topic =
      section === 'passphrase' ? 'guest-settings-passphrase'
        : section === 'recovery' ? 'guest-settings-recovery'
          : section === 'notifications' ? 'guest-settings-notifications'
            : section === 'totp' ? 'guest-settings-totp'
              : 'guest-settings';
    page.setAttribute('data-help-context', topic);
  };
  syncHelpContext();
  page.querySelector('#guest-settings-passphrase-section .module-section__header-main h2')
    ?.append(createHelpTip('guest-settings-passphrase'));
  page.querySelector('#guest-settings-recovery-section .module-section__header-main h2')
    ?.append(createHelpTip('guest-settings-recovery'));
  page.querySelector('#guest-settings-notifications-section .module-section__header-main h2')
    ?.append(createHelpTip('guest-settings-notifications'));
  page.querySelector('#guest-settings-totp-section .module-section__header-main h2')
    ?.append(createHelpTip('guest-settings-totp'));

  const busy = { titleKey: 'status.loading', detailKey: 'guest.signup.working' };
  const passphraseForm = page.querySelector('#guest-settings-passphrase-form') as HTMLFormElement;
  const passphraseError = page.querySelector('#guest-settings-passphrase-error') as HTMLElement;
  const passphraseSuccess = page.querySelector('#guest-settings-passphrase-success') as HTMLElement;
  const passphrasePolicyHint = page.querySelector('#guest-settings-passphrase-policy-hint') as HTMLElement;
  const passphraseExpired = page.querySelector('#guest-settings-passphrase-expired') as HTMLElement;
  const passphraseNewInput = passphraseForm.querySelector('input[name="new"]') as HTMLInputElement;
  const passphraseConfirmInput = passphraseForm.querySelector('input[name="confirm"]') as HTMLInputElement;
  let passphrasePolicy = defaultPolicyForTarget('guestVaultPassphrase');
  passphraseExpired.hidden = !isForcePassphraseChangeRequired();
  void resolvePasswordPolicy('guestVaultPassphrase').then((policy) => {
    passphrasePolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [passphraseNewInput, passphraseConfirmInput],
      hint: passphrasePolicyHint,
    });
  });
  attachPassphraseWhitespaceWarnings(
    passphraseForm.querySelector('input[name="current"]') as HTMLInputElement,
    passphraseNewInput,
    passphraseConfirmInput,
  );
  passphraseForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      passphraseError.hidden = true;
      passphraseSuccess.hidden = true;
      const data = new FormData(passphraseForm);
      const current = String(data.get('current') ?? '');
      const next = String(data.get('new') ?? '');
      const confirm = String(data.get('confirm') ?? '');
      if (next !== confirm) {
        passphraseError.hidden = false;
        passphraseError.textContent = t('settings.passphrase.mismatch');
        return;
      }
      const policyMessage = validatePasswordForUi(next, passphrasePolicy);
      if (policyMessage) {
        passphraseError.hidden = false;
        passphraseError.textContent = policyMessage;
        return;
      }
      setCryptoBusy(page, passphraseForm, true, busy);
      try {
        await changeVaultPassphrase(current, next, 'guestVaultPassphrase');
        const blob = decodeVaultBlobResponse(await api.getVaultBlob());
        const { loginVerifier } = await deriveGuestLoginVerifierFromPassphrase(next, blob.vaultSalt);
        await guestApi.updateLoginVerifier(encodeLoginVerifierBase64(loginVerifier));
        passphraseSuccess.hidden = false;
        passphraseForm.reset();
        setForcePassphraseChange(false);
        passphraseExpired.hidden = true;
      } catch (cause) {
        console.error(cause);
        passphraseError.hidden = false;
        passphraseError.textContent =
          passwordPolicyErrorFromCause(cause, passphrasePolicy)
          ?? t('settings.passphrase.invalidCurrent');
      } finally {
        setCryptoBusy(page, passphraseForm, false, busy);
      }
    })();
  });

  const recoveryForm = page.querySelector('#guest-settings-recovery-form') as HTMLFormElement;
  attachPassphraseWhitespaceWarnings(
    recoveryForm.querySelector('input[name="passphrase"]') as HTMLInputElement,
  );
  const recoveryStatus = page.querySelector('#guest-settings-recovery-status') as HTMLElement;
  const recoveryError = page.querySelector('#guest-settings-recovery-error') as HTMLElement;
  const refreshRecoveryStatus = async () => {
    try {
      const status = await api.getVaultStatus();
      recoveryStatus.textContent = status.recoveryActive
        ? t('settings.recovery.statusActive', { generation: String(status.recoveryGeneration) })
        : t('settings.recovery.statusInactive');
    } catch {
      recoveryStatus.textContent = '';
    }
  };
  void refreshRecoveryStatus();
  recoveryForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      recoveryError.hidden = true;
      const passphrase = String(new FormData(recoveryForm).get('passphrase') ?? '');
      const vault = loadVaultSession();
      if (!vault) {
        recoveryError.hidden = false;
        recoveryError.textContent = t('settings.recovery.locked');
        return;
      }
      setCryptoBusy(page, recoveryForm, true, busy);
      try {
        const blob = decodeVaultBlobResponse(await api.getVaultBlob());
        await unlockVaultWithPassphrase(passphrase, blob.vaultSalt, blob.vkWrapPassphrase, blob.vaultBlob, blob.vaultVersion);
        const session = await guestApi.session();
        const result = await createAndPublishRecoveryPackage(session.userId, vault.vaultKey);
        await showRecoveryExportDialog(result.exportText);
        await refreshRecoveryStatus();
        recoveryForm.reset();
      } catch (cause) {
        console.error(cause);
        recoveryError.hidden = false;
        recoveryError.textContent = t('status.error');
      } finally {
        setCryptoBusy(page, recoveryForm, false, busy);
      }
    })();
  });

  const notificationsForm = page.querySelector('#guest-settings-notifications-form') as HTMLFormElement;
  const notificationsError = page.querySelector('#guest-settings-notifications-error') as HTMLElement;
  const notificationsSuccess = page.querySelector('#guest-settings-notifications-success') as HTMLElement;
  const guestSedrDigestDaily = page.querySelector('#guest-settings-sedr-digest-daily') as HTMLInputElement;
  const guestSedrDigestBiweekly = page.querySelector('#guest-settings-sedr-digest-biweekly') as HTMLInputElement;
  const guestSedrDigestRooms = page.querySelector('#guest-settings-sedr-digest-rooms') as HTMLElement;

  const escapeAttr = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const applyGuestSedrDigests = (prefs: {
    dailyEnabled: boolean;
    biweeklyEnabled: boolean;
    rooms: { roomId: string; title: string; dailyEnabled: boolean; biweeklyEnabled: boolean }[];
  }) => {
    guestSedrDigestDaily.checked = prefs.dailyEnabled;
    guestSedrDigestBiweekly.checked = prefs.biweeklyEnabled;
    guestSedrDigestDaily.closest('label')?.setAttribute('title', t('settings.sedrDigests.daily.description'));
    guestSedrDigestBiweekly.closest('label')?.setAttribute('title', t('settings.sedrDigests.biweekly.description'));
    if (prefs.rooms.length === 0) {
      guestSedrDigestRooms.innerHTML = `<p class="muted settings-sedr-digests__empty" data-i18n="settings.sedrDigests.noRooms"></p>`;
      applyI18n(guestSedrDigestRooms);
      return;
    }
    guestSedrDigestRooms.innerHTML = prefs.rooms.map((room) => {
      const isCustom =
        room.dailyEnabled !== prefs.dailyEnabled
        || room.biweeklyEnabled !== prefs.biweeklyEnabled;
      return `
      <div class="settings-sedr-digests__room-row${isCustom ? ' settings-sedr-digests__room-row--custom' : ''}" data-room-id="${escapeAttr(room.roomId)}">
        <span class="settings-sedr-digests__room-title">${escapeAttr(room.title)}</span>
        <label class="settings-sedr-digests__col">
          <input type="checkbox" data-sedr-digest-room="daily" data-room-id="${escapeAttr(room.roomId)}" ${room.dailyEnabled ? 'checked' : ''} aria-label="${escapeAttr(t('settings.sedrDigests.daily.short'))}" />
        </label>
        <label class="settings-sedr-digests__col">
          <input type="checkbox" data-sedr-digest-room="biweekly" data-room-id="${escapeAttr(room.roomId)}" ${room.biweeklyEnabled ? 'checked' : ''} aria-label="${escapeAttr(t('settings.sedrDigests.biweekly.short'))}" />
        </label>
        <span class="settings-sedr-digests__col settings-sedr-digests__col--status">
          ${isCustom ? `<span class="settings-sedr-digests__badge" data-i18n="settings.sedrDigests.custom"></span>` : ''}
        </span>
      </div>`;
    }).join('');
    applyI18n(guestSedrDigestRooms);
  };

  void (async () => {
    try {
      const prefs = await api.getEmailNotifications();
      for (const type of GUEST_NOTIFICATION_TYPES) {
        const box = notificationsForm.querySelector(`input[data-notification-type="${type}"]`) as HTMLInputElement | null;
        if (!box) continue;
        const pref = prefs.preferences.find((p) => p.type === type);
        box.checked = pref?.enabled !== false;
      }
    } catch (cause) {
      console.error(cause);
    }
    try {
      applyGuestSedrDigests(await api.getSedrDigestPreferences());
    } catch (cause) {
      console.error(cause);
    }
  })();
  notificationsForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      notificationsError.hidden = true;
      notificationsSuccess.hidden = true;
      try {
        const prefs = await api.getEmailNotifications();
        const byType = new Map(prefs.preferences.map((p) => [p.type, p]));
        for (const type of GUEST_NOTIFICATION_TYPES) {
          const box = notificationsForm.querySelector(`input[data-notification-type="${type}"]`) as HTMLInputElement;
          const existing = byType.get(type);
          byType.set(type, existing ? { ...existing, enabled: box.checked } : { type, enabled: box.checked });
        }
        await api.putEmailNotifications([...byType.values()]);

        const roomUpdates = Array.from(
          guestSedrDigestRooms.querySelectorAll('div.settings-sedr-digests__room'),
        ).map((row) => {
          const roomId = (row as HTMLElement).dataset.roomId!;
          const daily = row.querySelector('input[data-sedr-digest-room="daily"]') as HTMLInputElement;
          const biweekly = row.querySelector('input[data-sedr-digest-room="biweekly"]') as HTMLInputElement;
          return {
            roomId,
            dailyEnabled: daily.checked,
            biweeklyEnabled: biweekly.checked,
          };
        });
        const digestUpdated = await api.putSedrDigestPreferences({
          dailyEnabled: guestSedrDigestDaily.checked,
          biweeklyEnabled: guestSedrDigestBiweekly.checked,
          rooms: roomUpdates,
        });
        applyGuestSedrDigests(digestUpdated);
        notificationsSuccess.hidden = false;
      } catch (cause) {
        console.error(cause);
        notificationsError.hidden = false;
        notificationsError.textContent = t('status.error');
      }
    })();
  });

  const totpStatus = page.querySelector('#guest-settings-totp-status') as HTMLElement;
  const totpEnroll = page.querySelector('#guest-settings-totp-enroll') as HTMLElement;
  const totpActions = page.querySelector('#guest-settings-totp-actions') as HTMLElement;
  const totpStart = page.querySelector('#guest-settings-totp-start') as HTMLButtonElement;
  const totpReplace = page.querySelector('#guest-settings-totp-replace') as HTMLButtonElement;
  const totpError = page.querySelector('#guest-settings-totp-error') as HTMLElement;
  const totpSuccess = page.querySelector('#guest-settings-totp-success') as HTMLElement;
  const totpSecret = page.querySelector('#guest-settings-totp-secret') as HTMLInputElement;
  const totpQr = page.querySelector('#guest-settings-totp-qr') as HTMLElement;
  const totpEnrollForm = page.querySelector('#guest-settings-totp-enroll-form') as HTMLFormElement;
  let pendingToken: string | null = null;
  let replaceMode = false;

  const refreshTotpUi = async () => {
    try {
      const status = await guestApi.totpStatus();
      totpStatus.setAttribute('data-i18n', status.enrolled ? 'guest.settings.totp.enrolled' : 'guest.settings.totp.subtitle');
      applyI18n(totpStatus);
      totpStart.hidden = status.enrolled;
      totpReplace.hidden = !status.enrolled;
      totpEnroll.hidden = true;
      totpActions.hidden = false;
    } catch { /* ignore */ }
  };
  void refreshTotpUi();

  const beginTotp = async (replace: boolean) => {
    totpError.hidden = true;
    totpSuccess.hidden = true;
    replaceMode = replace;
    try {
      const start = replace ? await guestApi.totpReplaceStart() : await guestApi.totpEnrollStart();
      pendingToken = start.pendingToken;
      totpSecret.value = start.secret ?? '';
      totpQr.innerHTML = start.otpauthUri ? renderSVG(start.otpauthUri, { ecc: 'M', pixelSize: 4 }) : '';
      totpEnroll.hidden = false;
      totpActions.hidden = true;
    } catch (cause) {
      console.error(cause);
      totpError.hidden = false;
      totpError.textContent = t('status.error');
    }
  };
  totpStart.addEventListener('click', () => void beginTotp(false));
  totpReplace.addEventListener('click', () => void beginTotp(true));
  totpEnrollForm.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      if (!pendingToken) return;
      totpError.hidden = true;
      const code = String(new FormData(totpEnrollForm).get('totpCode') ?? '');
      try {
        if (replaceMode) await guestApi.totpReplaceConfirm(pendingToken, code);
        else await guestApi.totpEnrollConfirm(pendingToken, code);
        totpSuccess.hidden = false;
        totpSuccess.textContent = t('guest.settings.totp.success');
        pendingToken = null;
        await refreshTotpUi();
      } catch (cause) {
        console.error(cause);
        totpError.hidden = false;
        totpError.textContent = t('sedr.guest.incorrectTotp');
      }
    })();
  });

  bindLocaleRefresh(() => applyI18n(page));
  return page;
}
